import { forwardRef, Inject, Injectable, Injector } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { BackgroundJobNotificationService } from 'Src/ng2/shared/services/background-job-notification-service/background-job-notification-service';
import { SnackBarService } from './../../shared/services/snackbar/snackbar.service';
import { ModalsService } from '../../shared/modals/modals.service';
import { BackgroundJob } from '../../shared/services/background-job/background-job.service';
import * as studentActivitiesActions from '../actions/student-activities-actions';
import { ApiService } from './../../shared/services/api-service/api-service';
import { ICreateStudentActivityParams } from './../../shared/typings/interfaces/student-activity.interface';

@Injectable()
export class StudentActivitiesEffects {
  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private injector: Injector,
    private backgroundJob: BackgroundJob,
    @Inject(forwardRef(() => ModalsService)) private modalsService: ModalsService,
    private snackBarService: SnackBarService,
    private backgroundJobNotificationService: BackgroundJobNotificationService,
  ) {}

  @Effect()
  loadStudentActivities$ = this.actions$.pipe(
    ofType(studentActivitiesActions.LOAD_STUDENT_ACTIVITIES),
    switchMap((action: { type: string; payload: any }) => {
      const { schoolId, where } = action.payload;
      return this.apiService.getStudentActivities({ schoolId, where }).pipe(
        switchMap((studentActivities: any) => [
          new studentActivitiesActions.LoadStudentActivitiesSuccess(studentActivities),
        ]),
        catchError(error => of(new studentActivitiesActions.LoadStudentActivitiesFail(error))),
      );
    }),
  );

  @Effect()
  updateStudentActivity$ = this.actions$.pipe(
    ofType(studentActivitiesActions.UPDATE_STUDENT_ACTIVITY),
    switchMap((action: { type: string; payload: any }) => {
      const { studentActivityId, patch } = action.payload;

      return this.apiService.patchStudentActivity({ studentActivityId, patch }).pipe(
        switchMap((studentActivity: any) => {
          const payload = {
            studentActivityId,
            patch,
          };
          return [new studentActivitiesActions.UpdateStudentActivitySuccess(payload)];
        }),
        catchError(error => {
          return of(new studentActivitiesActions.UpdateStudentActivityFail(error));
        }),
      );
    }),
  );

  @Effect()
  createStudentActivity$ = this.actions$.pipe(
    ofType(studentActivitiesActions.CREATE_STUDENT_ACTIVITY),
    switchMap((action: { type: string; payload: { studentActivityParams: ICreateStudentActivityParams } }) => {
      const { studentActivityParams } = action.payload;

      return this.apiService.createStudentActivity(studentActivityParams).pipe(
        switchMap((studentActivity: any) => {
          return [new studentActivitiesActions.CreateStudentActivitySuccess(studentActivity)];
        }),
        catchError(error => {
          return of(new studentActivitiesActions.CreateStudentActivityFail(error));
        }),
      );
    }),
  );

  @Effect({ dispatch: false })
  bulkUpdateStudentActivities$ = this.actions$.pipe(
    ofType(studentActivitiesActions.BULK_UPDATE_STUDENT_ACTIVITIES),
    switchMap((action: any) => {
      const { payload } = action;
      const backgroundJobSpinner = this.injector.get('BackgroundJobSpinner');
      return this.apiService.bulkUpdateStudentActivities(payload).pipe(
        switchMap((response: { data: { bulkUpdateStudentActivity: { backgroundJobId: string } } }) => {
          const {
            data: {
              bulkUpdateStudentActivity: { backgroundJobId },
            },
          } = response;
          const promise = (this.backgroundJob as any)(backgroundJobId).promise();
          backgroundJobSpinner.activateSpinner('Updating activities...', promise, null);
          return of(promise);
        }),
        tap(promise => {
          promise.then(() => {
            this.backgroundJobNotificationService.sendMessage({ backgroundJob: 'BulkStudentActivityUpdate' });
            this.snackBarService.showBatchActionToast({ success: true });
          });
        }),
        catchError(error => {
          // open modal with error
          const payload = {
            title: 'Unable to Complete Batch Action',
            message: 'The batch action could not be applied to all of the selected students. Please try again.',
            confirmText: 'Ok',
          };
          this.modalsService.openErrorModal(payload);
          return of(new studentActivitiesActions.BulkUpdateStudentActivitiesFail(error));
        }),
      );
    }),
  );
}
